import currency from "currency.js";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from ".";
import {
  SideResponse,
  GetOrders200Response,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { BACKGROUND_COLORS, COLORS } from "../../constants/design/colors";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { useSpotOrder } from "../../hooks/api/order/useSpotOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { nanosToSeconds } from "../../utils/date";
import { formatSizePrecision } from "../../utils/format";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { IBaseModalProps } from "../BaseModal";
import { Chip } from "../shared/Chip";
import { Spinner } from "../shared/Spinner";
import { FooterContainer, StatContainer } from "./style";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";

interface IRemoveOrderModalProps extends IBaseModalProps {
  order?: GetOrders200Response;
}

const isInstrumentType = (
  value: unknown,
  instrumentType: InstrumentTypeResponse
) => String(value || "").toUpperCase() === instrumentType;

/**
 * The modal responsible for removing orders
 */
function RemoveOrderModal({
  order,
  onHide,
  show,
  ...modalProps
}: IRemoveOrderModalProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { playSound } = useSFX();
  const { t } = useTranslation("app", {
    keyPrefix: "ConfirmationModal.RemoveOrderModal",
  });
  const { addToast } = useToast();
  const { removeOrder } = useOrder();
  const { cancelPerpOrder } = usePerpOrder();
  const { cancelSpotOrder } = useSpotOrder();
  const { data: accountData } = useGetAccount();
  const { resolveMarket, resolveMarketPrecision } = useInstrumentResolver();
  const { t: apiError } = useTranslation("apiErrors");

  // Whenever the modal is presented, reset state.
  useEffect(() => {
    if (show) {
      setErrorMessage(undefined);
    }
  }, [show, onHide]);

  const onRemoveOrder = useCallback(async () => {
    if (!order) {
      return;
    }

    try {
      setErrorMessage(undefined);
      setLoading(true);
      const market = resolveMarket(order) as
        | {
            base_currency?: string;
            quote_currency?: string;
            instrument_type?: InstrumentTypeResponse;
          }
        | undefined;
      const instrumentType =
        market?.instrument_type || order.instrument_type;

      if (
        isInstrumentType(instrumentType, InstrumentTypeResponse.Spot) &&
        market?.base_currency &&
        market?.quote_currency
      ) {
        await cancelSpotOrder({
          order_id: order.order_id,
          instrument_id: order.instrument_id,
          base_currency: market.base_currency,
          quote_currency: market.quote_currency,
        });
      } else if (
        isInstrumentType(instrumentType, InstrumentTypeResponse.Perpetual)
      ) {
        await cancelPerpOrder(order.order_id);
      } else if (isInstrumentType(instrumentType, InstrumentTypeResponse.Spot)) {
        throw new Error("Spot market metadata missing for wallet cancellation");
      } else if (!instrumentType) {
        throw new Error("Order market type could not be resolved");
      } else {
        await removeOrder(order.order_id);
      }
      playSound("order_cancelled", true);
      onHide?.();
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: t("order_edited_header"),
          subheader: t("order_edited_subheader"),
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } catch (error: any) {
      setErrorMessage(apiError(error.message) || t("remove_order_error"));
    } finally {
      setLoading(false);
    }
  }, [
    addToast,
    apiError,
    cancelPerpOrder,
    cancelSpotOrder,
    onHide,
    order,
    playSound,
    removeOrder,
    resolveMarket,
    t,
  ]);

  const currentPosition = useMemo(() => {
    if (!accountData) {
      return undefined;
    }
    return (accountData.positions || []).find(
      (pos) => pos.instrument_id === order?.instrument_id
    );
  }, [accountData, order?.instrument_id]);

  const stats = useMemo(() => {
    if (!order) {
      return [];
    }

    const items = [
      {
        title: t("order_id"),
        data: order.order_id ? `#${order.order_id}` : "-",
      },
      {
        title: t("bid_price"),
        data: currency(order.price, {
          precision: resolveMarketPrecision(order).price_precision,
        }).format(),
      },
      {
        title: t("contracts"),
        data: order.close_position
          ? t("close_position")
          : Number(order.amount).toFixed(2),
      },
      {
        title: t("total"),
        data: currency(Number(order.amount) * Number(order.price), {
          precision: resolveMarketPrecision(order).price_precision,
        }).format(),
      },
    ];

    if (order.side === SideResponse.Sell) {
      items.push({
        title: t("premium_earned"),
        data: currency(Number(order.amount) * Number(order.price), {
          precision: resolveMarketPrecision(order).price_precision,
        }).format(),
      });

      if (order.initial_margin) {
        items.push({
          title: t("margin_required"),
          data: currency(order.initial_margin).format(),
        });
      }
    }

    return items;
  }, [order, resolveMarketPrecision, t]);

  if (!order) {
    return null;
  }

  return (
    <ConfirmationModal
      title={
        order.side === SideResponse.Sell ? t("remove_offer") : t("remove_bid")
      }
      primaryColor={COLORS.blue.one}
      style={{
        backgroundColor: BACKGROUND_COLORS.seven,
      }}
      tag={{
        texts:
          order.expiry && order.strike
            ? [
                order.side === SideResponse.Buy ? t("buy") : t("sell"),
                order.order_type,
                moment.unix(nanosToSeconds(order.expiry)).format("DD MMM YY"),
                currency(order.strike, { precision: 0 }).format(),
              ]
            : [
                order.side === SideResponse.Buy ? t("buy") : t("sell"),
                order.order_type,
                order.instrument_name,
              ],
        color:
          order.side === SideResponse.Buy ? COLORS.up.one : COLORS.down.one,
        backgroundColor:
          order.side === SideResponse.Buy ? COLORS.up.five : COLORS.down.five,
      }}
      stats={stats}
      confirmationButtons={[
        {
          // eslint-disable-next-line no-nested-ternary
          title: loading ? (
            <Spinner />
          ) : order.side === SideResponse.Sell ? (
            t("remove_offer")
          ) : (
            t("remove_bid")
          ),
          onClick: onRemoveOrder,
        },
      ]}
      isLoading={loading}
      onHide={onHide}
      show={show}
      error={errorMessage}
      extras={
        <FooterContainer>
          <StatContainer>
            <span>
              {t("position")}
              {currentPosition && (
                <Chip
                  theme={
                    currentPosition.side === SideResponse.Buy ? "green" : "red"
                  }
                  value={
                    currentPosition.side === SideResponse.Buy
                      ? t("long")
                      : t("short")
                  }
                />
              )}
            </span>
            <span>
              {currentPosition
                ? formatSizePrecision(currentPosition.amount)
                : "---"}
            </span>
          </StatContainer>
        </FooterContainer>
      }
      {...modalProps}
    />
  );
}

export default RemoveOrderModal;
